/**
 * features/finance/OpeningBalances.tsx
 * ------------------------------------------------------------------
 * Taking the school's position onto the ledger, once.
 *
 * Every figure the accounts will ever produce is measured from this one
 * entry. It is posted a single time, on a single date, and if it is wrong
 * nothing downstream can be right — so this screen is built to be slow in
 * the places where being fast is dangerous.
 *
 * THE CUT-OVER DATE IS THE WHOLE DECISION. It is the day the portal becomes
 * the book of record: every invoice on or before it is carried by this
 * journal, everything after it posts through its own trigger from its own
 * source document. Move the date by one day and an invoice is either
 * counted twice or lost entirely. So the date is typed, what it selects is
 * shown before anything is posted, and the two sides are listed pupil by
 * pupil rather than summarised.
 *
 * GROSS ON BOTH SIDES. Families who owe the school and families whose money
 * the school is holding are shown, and posted, separately. The netted figure
 * appears here too — but only labelled as what a netted entry would have
 * claimed, because that number is on the wrong side of the balance sheet for
 * half of what it contains.
 *
 * A DISAGREEMENT IS A STOP, NOT A WARNING. If any invoice's stored balance
 * differs from total less payments less credits, the post button is refused
 * and the offending invoices are listed. Publishing an opening position over
 * a sub-ledger that disagrees with itself puts an unverified number at the
 * base of the accounts, and every report after it inherits the doubt.
 *
 * RE-POSTING IS SAFE AND SAID SO. The journal id derives from the cut-over
 * date, so posting the same date again overwrites the same journal instead
 * of adding a second. A DIFFERENT date is a different journal — which is
 * correct, and is exactly why the screen shows when one already exists.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  Landmark, AlertTriangle, CheckCircle2, Users, Wallet, Scale, Info, CalendarClock, Power,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { useGlData } from '@/features/finance/useGlData';
import { postJournal } from '@/features/finance/glWrite';
import { finance } from '@/features/finance/financeWrite';
import {
  buildOpeningPosition, openingJournalLines, openingSourceId, openingProblem,
  type OpeningRow,
} from '@/lib/openingBalances';
import { journalIdFor, type SystemKey } from '@/lib/gl';
import {
  PageHeader, SoftCard, StatChip, PanelHeader, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayIso = () => new Date().toISOString().slice(0, 10);

/** The three accounts the entry needs, by the key the chart was seeded with. */
const NEEDED: { key: SystemKey; role: string }[] = [
  { key: 'AR_FEES', role: 'Accounts Receivable — School Fees' },
  { key: 'FEES_IN_ADVANCE', role: 'Fees Received in Advance' },
  { key: 'ACCUM_FUND', role: 'Accumulated Fund' },
];

export default function OpeningBalances() {
  const { userData } = useAuth();
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const canPost = perms.canPostJournals;

  const [cutover, setCutover] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showRows, setShowRows] = useState<'debtors' | 'credits' | null>(null);

  const fin = useFinanceData();
  const fy = `FY${cutover.slice(0, 4)}`;
  const gl = useGlData(fy);

  const position = useMemo(
    () => buildOpeningPosition(fin.invoices, cutover),
    [fin.invoices, cutover],
  );

  /* Resolve the three accounts from the chart rather than hard-coding codes:
     the codes belong to the seeded chart, and a school that renumbers its
     accounts must not silently post to whatever now sits at 1200. */
  const accounts = useMemo(() => {
    const found: Partial<Record<SystemKey, string>> = {};
    for (const a of gl.accounts) if (a.systemKey) found[a.systemKey] = a.code;
    return found;
  }, [gl.accounts]);

  const missing = NEEDED.filter((n) => !accounts[n.key]);

  const lines = useMemo(() => {
    if (missing.length) return [];
    return openingJournalLines(position, {
      receivables: accounts.AR_FEES as string,
      feesInAdvance: accounts.FEES_IN_ADVANCE as string,
      accumulatedFund: accounts.ACCUM_FUND as string,
    });
  }, [position, accounts, missing.length]);

  const problem = openingProblem(position);
  const sourceId = openingSourceId(cutover);
  const existing = gl.journals.find((j) => j.id === journalIdFor('opening', sourceId));

  const blocked = !canPost || !!problem || missing.length > 0 || lines.length === 0;

  async function doPost() {
    setBusy(true);
    setMsg(null);
    try {
      const j = await postJournal({
        type: 'opening',
        sourceId,
        date: cutover,
        narration: `Opening position brought forward at ${cutover}`,
        lines,
        actor: { uid: userData?.uid, name: userData?.displayName, email: userData?.email },
      });
      /* THE SAME ACT SETS THE BOUNDARY. The posting triggers refuse anything
         dated on or before glCutoverDate because the opening journal already
         carried it. Writing the date here, in the same action that posts the
         entry, is what stops the two from drifting: a boundary kept in a
         second place is a boundary somebody eventually forgets to move. */
      await finance.settings.update({ glCutoverDate: cutover });
      setMsg({
        ok: true,
        text: `Posted as ${j.journalNo}. The ledger now opens with ${money(position.totalDebtors)} `
          + `owed to the school and ${money(position.totalCredits)} held on families’ behalf. `
          + `Documents dated on or before ${cutover} will not post again.`,
      });
      setConfirming(false);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  if (fin.loading || gl.loading) {
    return <div className="p-8 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Landmark}
        title="Opening Balances"
        subtitle="What the school is owed, and what it holds, on the day the ledger takes over"
      />

      {/* ── The date ───────────────────────────────────────────────── */}
      <SoftCard pad="p-0" hover={false}>
        <PanelHeader icon={CalendarClock} title="Cut-over date" />
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 max-w-3xl">
            The day the portal becomes the book of record. Every invoice issued on or before
            it is carried by this one journal; everything after it posts from its own source
            document. An invoice with no issue date is left out of both — it cannot be placed
            on either side of the boundary, and guessing would double-count it or lose it.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Cut-over
              </label>
              <input
                type="date"
                value={cutover}
                onChange={(e) => { setCutover(e.target.value); setMsg(null); setConfirming(false); }}
                className={glassFieldClass('py-2')}
              />
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 pb-2">
              Journal <span className="font-mono">{sourceId}</span>
            </div>
          </div>

          {existing && (
            <div className="flex gap-2 items-start rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>
                An opening journal for this date already exists — <strong>{existing.journalNo}</strong>,
                posted {String(existing.postedAt ?? '').slice(0, 10)}. Posting again replaces it
                with today&rsquo;s figures. Choosing a <em>different</em> date creates a second,
                separate opening journal, which is almost never what you want.
              </span>
            </div>
          )}
        </div>
      </SoftCard>

      {/* ── What that date selects ─────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip
          icon={Users}
          label={`Owed to the school — ${position.debtors.length} invoice${position.debtors.length === 1 ? '' : 's'}`}
          value={money(position.totalDebtors)}
        />
        <StatChip
          icon={Wallet}
          label={`Held for families — ${position.credits.length} in credit`}
          value={money(position.totalCredits)}
        />
        <StatChip
          icon={Scale}
          label="Net — NOT what is posted"
          value={money(position.net)}
          tone="slate"
        />
        <StatChip
          icon={CheckCircle2}
          label="Settled — carried by neither side"
          value={String(position.settledCount)}
        />
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 px-1 max-w-3xl">
        The net figure is shown for reference only. It is <strong>not</strong> what gets posted:
        money the school is holding for a family is a liability, not a smaller asset, and
        collapsing the two would understate both by the same amount.
      </p>

      {/* ── The stop ───────────────────────────────────────────────── */}
      {position.discrepancies.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <PanelHeader
            icon={AlertTriangle}
            tone="gold"
            title={`${position.discrepancies.length} invoice${position.discrepancies.length === 1 ? '' : 's'} disagree with themselves`}
          />
          <div className="p-5 space-y-3">
            <p className="text-sm text-red-700 dark:text-red-300">
              The stored balance differs from total less payments less credits. Nothing can be
              posted until these are settled — this entry is what every later figure is measured
              from.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-4">Invoice</th>
                    <th className="py-2 pr-4 text-right">Stored</th>
                    <th className="py-2 pr-4 text-right">Total − paid − credited</th>
                    <th className="py-2 text-right">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {position.discrepancies.map((d) => (
                    <tr key={d.invoiceId} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-4 font-mono text-xs">{d.invoiceNo ?? d.invoiceId}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(d.stored)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(d.computed)}</td>
                      <td className="py-2 text-right tabular-nums text-red-600 dark:text-red-400">
                        {money(d.stored - d.computed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SoftCard>
      )}

      {missing.length > 0 && (
        <SoftCard pad="p-5" hover={false}>
          <div className="flex gap-2 items-start text-sm text-red-700 dark:text-red-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">The chart of accounts is not ready for this entry.</p>
              <ul className="mt-1 list-disc pl-5">
                {missing.map((m) => (
                  <li key={m.key}>
                    {m.role} — no account carries <span className="font-mono">{m.key}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </SoftCard>
      )}

      {/* ── Live posting ───────────────────────────────────────────── */}
      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={Power}
          title="Live posting"
          subtitle="Whether invoices, payments and credit notes reach the ledger by themselves"
        />
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300 max-w-3xl">
            Two switches, on purpose. Posting the opening journal records{' '}
            <em>where</em> the boundary is; this one says <em>whether</em> the triggers are
            live. Bringing the position across must not silently start posting every invoice
            the school raises, so turning this on is its own decision.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
              fin.settings.glPostingEnabled
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}
            >
              {fin.settings.glPostingEnabled ? 'Posting is live' : 'Posting is off'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Cut-over on record:{' '}
              <span className="font-mono">{fin.settings.glCutoverDate ?? 'not set'}</span>
            </span>
            {canPost && (
              <button
                type="button"
                className={btnGhost}
                onClick={() => {
                  void finance.settings.update({
                    glPostingEnabled: !fin.settings.glPostingEnabled,
                  });
                }}
              >
                {fin.settings.glPostingEnabled ? 'Switch posting off' : 'Switch posting on'}
              </button>
            )}
          </div>
          {!fin.settings.glCutoverDate && (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              No cut-over is on record, so nothing will post even with this on. An absent date
              is not &ldquo;post everything&rdquo; — it is a school that has not yet said where
              its books change hands.
            </p>
          )}
        </div>
      </SoftCard>

      {/* ── Who is on each side ────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SideList
          icon={Users}
          title="Families who owe the school"
          rows={position.debtors}
          total={position.totalDebtors}
          open={showRows === 'debtors'}
          onToggle={() => setShowRows(showRows === 'debtors' ? null : 'debtors')}
        />
        <SideList
          icon={Wallet}
          title="Families whose money the school holds"
          rows={position.credits}
          total={position.totalCredits}
          open={showRows === 'credits'}
          onToggle={() => setShowRows(showRows === 'credits' ? null : 'credits')}
        />
      </div>

      {/* ── The journal ────────────────────────────────────────────── */}
      <SoftCard pad="p-0" hover={false}>
        <PanelHeader icon={Scale} title="The entry" subtitle="Gross both sides, and it must balance" />
        <div className="p-5 space-y-4">
          {lines.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {problem ?? 'Nothing to post.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-4">Account</th>
                    <th className="py-2 pr-4">Narration</th>
                    <th className="py-2 pr-4 text-right">Debit</th>
                    <th className="py-2 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`${l.accountCode}-${i}`} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-4">
                        <span className="font-mono text-xs">{l.accountCode}</span>{' '}
                        <span className="text-gray-600 dark:text-gray-300">
                          {gl.byCode.get(l.accountCode)?.name ?? ''}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-gray-500 dark:text-gray-400">{l.narration}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{l.dr ? money(l.dr) : ''}</td>
                      <td className="py-2 text-right tabular-nums">{l.cr ? money(l.cr) : ''}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-2 pr-4" colSpan={2}>Total</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {money(lines.reduce((a, l) => a + l.dr, 0))}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {money(lines.reduce((a, l) => a + l.cr, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {problem && lines.length > 0 && (
            <p className="text-sm text-red-700 dark:text-red-300">{problem}</p>
          )}
          {!canPost && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You can read this position but not post it. Posting the opening journal is a
              bursar or administrator act.
            </p>
          )}

          {msg && (
            <div
              className={`rounded-lg px-4 py-3 text-sm ${msg.ok
                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
            >
              {msg.text}
            </div>
          )}

          <div className="flex justify-end gap-2">
            {confirming ? (
              <>
                <button type="button" className={btnGhost} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={btnPrimary}
                  disabled={busy}
                  onClick={() => { void doPost(); }}
                >
                  {busy ? 'Posting…' : `Yes — open the ledger at ${cutover}`}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={btnPrimary}
                disabled={blocked}
                onClick={() => { setMsg(null); setConfirming(true); }}
              >
                {existing ? 'Replace the opening journal' : 'Post the opening journal'}
              </button>
            )}
          </div>

          {confirming && (
            <p className="text-sm text-amber-700 dark:text-amber-300 text-right">
              This states that the portal becomes the book of record on <strong>{cutover}</strong>,
              carrying {position.debtors.length + position.credits.length} accounts.
            </p>
          )}
        </div>
      </SoftCard>
    </div>
  );
}

/**
 * One side of the position, pupil by pupil.
 *
 * Collapsed by default and expandable in FULL — not paged and not truncated.
 * A bursar checking an opening position needs to be able to read all of it,
 * and a list that quietly stops at fifty is how a wrong figure gets signed
 * off.
 */
function SideList({
  icon, title, rows, total, open, onToggle,
}: {
  icon: LucideIcon;
  title: string;
  rows: OpeningRow[];
  total: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <SoftCard pad="p-0" hover={false}>
      <PanelHeader icon={icon} title={title} />
      <div className="p-5">
        <div className="flex items-baseline justify-between">
          <div className="text-2xl font-semibold tabular-nums text-gray-800 dark:text-gray-100">
            {money(total)}
          </div>
          <button type="button" className={btnGhost} onClick={onToggle}>
            {open ? 'Hide' : `Show all ${rows.length}`}
          </button>
        </div>
        {open && (
          <div className="mt-4 max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.invoiceId} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-1.5 pr-3">{r.studentName ?? r.studentId}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-gray-400">{r.invoiceNo ?? ''}</td>
                    <td className="py-1.5 text-right tabular-nums">{money(Math.abs(r.balance))}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td className="py-2 text-gray-400">None.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SoftCard>
  );
}
